import { Params } from '@angular/router';
import { AppService } from '@services/app/app.service';
import { AuthService } from '@services/auth/auth.service';
import { NavigatorService } from '@services/navigator/navigator.service';
import { ThemeService } from '@services/theme/theme.service';
import { WizardNavigationService } from '@services/wizard-navigation/wizard-navigation.service';
import { WizardService } from '@services/wizard/wizard.service';
import { PositionService } from '@services/position/position.service';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { ERRORS } from '@core/models';
import { ThemeCode } from '@core/models';

@Injectable({
  providedIn: 'root'
})
export class OperationService {
  private positionId: string;

  private wizardCode: string;

  private specificActionParams: Params;

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private navigatorService: NavigatorService,
    private positionService: PositionService,
    private wizardService: WizardService,
    private wizardNav: WizardNavigationService,
    private themeService: ThemeService
  ) {}

  /**
   * imposta il setup recuperando la position id e il wizard code definiti dallo user data
   */
  setupFromAuth(): void {
    const positionId = this.authService.getAuthUserAttributes().positionId;
    const wizardCode = this.authService.getAuthUserAttributes().operation;

    this.setup(positionId, wizardCode);
  }

  /**
   * imposta il setup con position richiesta e wizard comune di allestimento
   */
  setupAsCommonPreparation(positionId: string): void {
    this.setup(positionId, 'ALLESTIMENTO');
  }

  /**
   * imposta la position e il wizard code con su cui avviare il processo
   */
  setup(positionId: string, wizardCode: string, themeCode?: ThemeCode): void {
    this.positionId = positionId;
    this.wizardCode = wizardCode;

    const themes = this.appService.getConfig().wizard.themes;

    if (themeCode) {
      this.themeService.setAreaTheme(themeCode);
    } else if (themes && themes[wizardCode]) {
      this.themeService.setAreaTheme(themes[wizardCode]);
    } else {
      this.themeService.setDefaultAreaTheme();
    }
  }

  /**
   * avvia processo caricando pratica e wizard e naviga verso la pagina iniziale
   */
  startAndOpen(): Observable<void> {
    return this.start().pipe(
      mergeMap(() => from(this.navigateToStart())),
      catchError(err => {
        this.navigateToError();

        return throwError(err);
      })
    );
  }

  /**
   * avvia processo caricando pratica e wizard
   */
  start(): Observable<boolean> {
    return this.checkSetup().pipe(
      mergeMap(() => this.loadPosition()),
      mergeMap(() => this.loadWizard()),
      map(() => true)
    );
  }

  /**
   * avvia processo caricando pratica e wizard solo se non risultano già caricati
   */
  resume(): Observable<boolean> {
    return this.checkSetup().pipe(
      mergeMap(() => this.resumePosition()),
      mergeMap(() => this.resumeWizard())
    );
  }

  /**
   * stabilisce se è necessario caricare i dati della position o del wizard prima di continuare
   */
  isResumeNeeded(): boolean {
    return !this.isPositionLoaded() || !this.isWizardLoaded();
  }

  /**
   * controlla se il setup è impostato correttamente
   */
  isSetupValid(): boolean {
    return !!this.positionId && !!this.wizardCode;
  }

  /**
   * stabilisce se il setup corrisponde ad un comune processo di allestimento
   */
  isCommonPreparationSetup(): boolean {
    return this.wizardCode === 'ALLESTIMENTO';
  }

  /**
   * stabilisce se il setup corrisponde ad un processo di handover
   */
  isHandoverSetup(): boolean {
    return (
      this.wizardCode && this.wizardCode.toLowerCase().includes('handover')
    );
  }

  /**
   * naviga alla pagina iniziale del processo
   */
  navigateToStart(): Promise<void> {
    this.readSpecificActionParams();
    this.cleanWizardNavigationData();

    if (this.isSpecificActionNeeded()) {
      return this.navigateToSpecificAction().then(() =>
        this.cleanSpecificActionParams()
      );
    } else {
      return this.navigateToWizardStart();
    }
  }

  /**
   * naviga alla pagina di errore
   */
  navigateToError(): Promise<boolean> {
    return this.navigatorService.navigateToError(ERRORS.invalidProcess);
  }

  /**
   * ricava il position id e il wizard code dalla sessione
   */
  private checkSetup(): Observable<boolean> {
    if (!this.isSetupValid()) {
      return throwError('wrn.process_loading_error');
    }

    return of(true);
  }

  /**
   * carica la position
   */
  private loadPosition(): Observable<boolean> {
    return this.positionService.loadPosition(this.positionId);
  }

  /**
   * carica position solo se non risulta già caricata
   */
  private resumePosition(): Observable<boolean> {
    if (!this.isPositionLoaded()) {
      return this.loadPosition();
    }

    return of(true);
  }

  /**
   * determina se la position è caricata correttamente
   * e coincide con il position id definito nella sessione
   */
  private isPositionLoaded(): boolean {
    const position = this.positionService.getPosition();

    return !!position && position.data.positionId === this.positionId;
  }

  /**
   * carica wizard
   */
  private loadWizard(): Observable<boolean> {
    return this.wizardService.loadWizard(this.wizardCode);
  }

  /**
   * carica wizard solo se non risulta già caricata
   */
  private resumeWizard(): Observable<boolean> {
    if (!this.isWizardLoaded()) {
      return this.loadWizard();
    }

    return of(true);
  }

  /**
   * determina se il wizard è caricato correttamente
   * e coincide con il wizard code definito nella sessione
   */
  private isWizardLoaded(): boolean {
    const wizard = this.wizardService.getWizard();

    return !!wizard && wizard.data.wizardCode === this.wizardCode;
  }

  /**
   * naviga verso lo step determinato dall'azione specifica richiesta
   */
  private navigateToSpecificAction(): Promise<void> {
    const stepToOpen = this.specificActionParams.open;
    const closePrevious = this.specificActionParams.closePrevious;
    const params = { ...this.specificActionParams };

    if (closePrevious) {
      return this.completeStepsBeforeAndNavigateToStep(stepToOpen, params);
    } else {
      return this.navigateToStep(stepToOpen, params);
    }
  }

  /**
   * chiude gli step precedenti e naviga verso lo step designato
   */
  private completeStepsBeforeAndNavigateToStep(
    stepCode: string,
    params?: Params
  ): Promise<void> {
    return this.wizardService
      .completeStepsBefore(stepCode)
      .toPromise()
      .then(() => this.navigateToStep(stepCode, params));
  }

  /**
   * naviga verso lo step designato
   */
  private navigateToStep(stepCode: string, params?: Params): Promise<void> {
    return this.wizardNav.navigateToStepByCode(stepCode, params);
  }

  /**
   * naviga all'inizio del wizard
   */
  private navigateToWizardStart(): Promise<void> {
    return this.wizardNav.navigateToStart();
  }

  /**
   * pulisce i dati navigazione eventualmente impostati sul wizard
   * per evitare conflitti di navigazione
   */
  private cleanWizardNavigationData(): void {
    this.wizardNav.cleanNavigationData();
  }

  /**
   * legge i parametri impostati per un eventuale azione specifica
   */
  private readSpecificActionParams(): void {
    this.specificActionParams = this.navigatorService.usePathActions([
      'urlBack'
    ]);
  }

  /**
   * cancella i parametri per l'azione specifica
   */
  private cleanSpecificActionParams(): void {
    this.specificActionParams = undefined;
  }

  /**
   * stabilisce se è richiesta un'azione specifica
   */
  private isSpecificActionNeeded(): boolean {
    return !!this.specificActionParams && !!this.specificActionParams.open;
  }
}
